/**
 * Cart duck — manages shopping cart state for ePro (and future WRI) items.
 *
 * Cart items are persisted to localStorage so they survive page refreshes.
 *
 * Item shape:
 * {
 *   listingId: string,          // Sharetribe listing UUID
 *   title: string,
 *   price: number,              // price in cents
 *   currency: string,           // e.g. 'USD'
 *   imageUrl: string|null,
 *   source: 'epro'|'wri',
 *   slug: string,               // for constructing the listing URL
 *   quantity: number,
 *   currentStock: number|null,  // available stock at add-to-cart time; null means unlimited
 *   shippingMethod: string,     // 'flat' | 'buyer' | 'free' | '' (from publicData.shippingMethod)
 *   flatAmountInCents: number,  // flat shipping fee in cents (when shippingMethod='flat')
 *   parcel: object|null,        // { length, width, height, weight } for Shippo (when shippingMethod='buyer')
 * }
 */

const CART_STORAGE_KEY = 'bowtackle_cart_v1';

// ================ Helpers ================ //

const loadFromStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const saveToStorage = items => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    // ignore quota errors
  }
};

// ================ Action types ================ //

export const CART_ADD_ITEM = 'app/cart/ADD_ITEM';
export const CART_REMOVE_ITEM = 'app/cart/REMOVE_ITEM';
export const CART_UPDATE_QUANTITY = 'app/cart/UPDATE_QUANTITY';
export const CART_CLEAR = 'app/cart/CLEAR';

// ================ Reducer ================ //

const initialState = {
  items: loadFromStorage(),
};

export default function cartReducer(state = initialState, action = {}) {
  switch (action.type) {
    case CART_ADD_ITEM: {
      const existing = state.items.find(i => i.listingId === action.payload.listingId);
      let newItems;
      if (existing) {
        const stock = action.payload.currentStock ?? existing.currentStock ?? Infinity;
        newItems = state.items.map(i =>
          i.listingId === action.payload.listingId
            ? { ...i, quantity: Math.min(stock, i.quantity + action.payload.quantity), currentStock: action.payload.currentStock ?? i.currentStock }
            : i
        );
      } else {
        newItems = [...state.items, action.payload];
      }
      saveToStorage(newItems);
      return { ...state, items: newItems };
    }

    case CART_REMOVE_ITEM: {
      const newItems = state.items.filter(i => i.listingId !== action.payload);
      saveToStorage(newItems);
      return { ...state, items: newItems };
    }

    case CART_UPDATE_QUANTITY: {
      const newItems = state.items.map(i => {
        if (i.listingId !== action.payload.listingId) return i;
        const max = i.currentStock ?? Infinity;
        return { ...i, quantity: Math.min(max, Math.max(1, action.payload.quantity)) };
      });
      saveToStorage(newItems);
      return { ...state, items: newItems };
    }

    case CART_CLEAR: {
      saveToStorage([]);
      return { ...state, items: [] };
    }

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const addToCart = item => ({ type: CART_ADD_ITEM, payload: item });
export const removeFromCart = listingId => ({ type: CART_REMOVE_ITEM, payload: listingId });
export const updateCartQuantity = (listingId, quantity) => ({
  type: CART_UPDATE_QUANTITY,
  payload: { listingId, quantity },
});
export const clearCart = () => ({ type: CART_CLEAR });

// ================ Selectors ================ //

export const getCartItems = state => state.cart?.items || [];
export const getCartItemCount = state =>
  (state.cart?.items || []).reduce((sum, i) => sum + i.quantity, 0);
export const getCartSubtotal = state =>
  (state.cart?.items || []).reduce((sum, i) => sum + (i.price || i.cost || 0) * i.quantity, 0);
